import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import dashboardService, { 
    type StockStatistics, 
    type CategoryData, 
    type SubCategoryData, 
    type RevenueComparisonData,
    type PeakHourData
} from '../services/dashboardService';
import { useAuth } from './AuthContext';

interface LoadingState {
    stockStatistics: boolean;
    categories: boolean;
    subcategories: boolean;
    peakHours: boolean;
    revenue: boolean;
}

interface DashboardContextType {
    stockStatistics: StockStatistics | null;
    categoriesData: CategoryData[];
    subcategoriesData: SubCategoryData[];
    revenueData: RevenueComparisonData | null;
    peakHourData: PeakHourData[];
    loadingStates: LoadingState;
    error: string | null;
    fetchDashboardData: () => Promise<void>;
    fetchRevenueData: (options: {
        startDate: Date;
        endDate: Date;
        comparisonStartDate: Date;
        comparisonEndDate: Date;
        isYearly?: boolean;
    }) => Promise<void>;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export const useDashboard = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error('useDashboard must be used within a DashboardProvider');
    }
    return context;
};

interface DashboardProviderProps {
    children: ReactNode;
}

export function DashboardProvider({ children }: { children: React.ReactNode }) {
    const { isAuthenticated } = useAuth();
    const [stockStatistics, setStockStatistics] = useState<StockStatistics | null>(null);
    const [categoriesData, setCategoriesData] = useState<CategoryData[]>([]);
    const [subcategoriesData, setSubcategoriesData] = useState<SubCategoryData[]>([]);
    const [revenueData, setRevenueData] = useState<RevenueComparisonData | null>(null);
    const [peakHourData, setPeakHourData] = useState<PeakHourData[]>([]);
    const [loadingStates, setLoadingStates] = useState<LoadingState>({
        stockStatistics: false,
        categories: false,
        subcategories: false,
        peakHours: false,
        revenue: false
    });
    const [error, setError] = useState<string | null>(null);
    
    const fetchStockStatistics = useCallback(async () => {
        try {
            setLoadingStates(prev => ({ ...prev, stockStatistics: true }));
            const stats = await dashboardService.getStockStatistics();
            setStockStatistics(stats);
        } catch (err) {
                        setError(err instanceof Error ? err.message : "Failed to fetch stock statistics");
        } finally {
            setLoadingStates(prev => ({ ...prev, stockStatistics: false }));
        }
    }, []);

    const fetchCategories = useCallback(async () => {
        try {
            setLoadingStates(prev => ({ ...prev, categories: true }));
            const categories = await dashboardService.getAllCategoriesProductsAndOrders();
            setCategoriesData(categories);
        } catch (err) {
                        setError(err instanceof Error ? err.message : "Failed to fetch categories");
        } finally {
            setLoadingStates(prev => ({ ...prev, categories: false }));
        }
    }, []);

    const fetchSubcategories = useCallback(async () => {
        try {
            setLoadingStates(prev => ({ ...prev, subcategories: true }));
            const subcategories = await dashboardService.getAllSubcategoriesProductsAndOrders();
            setSubcategoriesData(subcategories);
        } catch (err) {
                        setError(err instanceof Error ? err.message : "Failed to fetch subcategories");
        } finally {
            setLoadingStates(prev => ({ ...prev, subcategories: false }));
        }
    }, []);

    const fetchPeakHours = useCallback(async () => {
        try {
            setLoadingStates(prev => ({ ...prev, peakHours: true }));
            const peakHours = await dashboardService.getPeakHourData();
            setPeakHourData(peakHours);
        } catch (err) {
                        setError(err instanceof Error ? err.message : "Failed to fetch peak hours");
        } finally {
            setLoadingStates(prev => ({ ...prev, peakHours: false }));
        }
    }, []);

    const fetchDashboardData = useCallback(async () => {
        if (!isAuthenticated) return;

        try {
            setError(null);
            await Promise.all([
                fetchStockStatistics(),
                fetchCategories(),
                fetchSubcategories(),
                fetchPeakHours()
            ]);
        } catch (err) {
                        setError(err instanceof Error ? err.message : "Failed to fetch dashboard data");
        }
    }, [isAuthenticated, fetchStockStatistics, fetchCategories, fetchSubcategories, fetchPeakHours]);

    const fetchRevenueData = useCallback(async (options: {
        startDate: Date;
        endDate: Date;
        comparisonStartDate: Date;
        comparisonEndDate: Date;
        isYearly?: boolean;
    }) => {
        try {
            setLoadingStates(prev => ({ ...prev, revenue: true }));
            setError(null);

            const data = await dashboardService.getRevenueComparisonData(options);
            if (!data) {
                setError('No revenue data available for the selected period');
                setRevenueData({
                    current: [],
                    previous: []
                });
            } else {
                setRevenueData(data);
            }
        } catch (err) {
                        setError(err instanceof Error ? err.message : "Failed to fetch revenue data");
            setRevenueData({
                current: [],
                previous: []
            });
        } finally {
            setLoadingStates(prev => ({ ...prev, revenue: false }));
        }
    }, []);

    // Initial data fetch
    useEffect(() => {
        if (isAuthenticated) {
            fetchDashboardData();
        }
    }, [isAuthenticated, fetchDashboardData]);

    const value = {
        stockStatistics,
        categoriesData,
        subcategoriesData,
        revenueData,
        peakHourData,
        loadingStates,
        error,
        fetchDashboardData,
        fetchRevenueData
    };

    return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
} 